import React, { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";

import ProfileDetails from "../components/login/ProfileDetails";
import Loader from "../components/Loader";
import { getUserId } from "shared-frontend/functions/auth";
import { receiveUser } from "shared-frontend/redux/actions/user";
import {
	getProfileQueryKey,
	useProfile,
	usePatchProfile,
} from "../api/generated/customer/customer";

/**
 * First-login step where the user enters their first and last name.
 *
 * Server state is owned by TanStack Query: {@link useProfile} seeds the form and
 * {@link usePatchProfile} performs the save. The form is gated on the profile query so it
 * only mounts once the user's existing name is available — rendering it earlier would seed
 * the inputs with empty strings that never update. The profile is still mirrored into the
 * app-wide Redux `state.user` via `receiveUser` (and eligibility is reported to GTM once per
 * page load) so the rest of the app — which reads the profile from Redux — keeps working. After
 * a successful save the profile query is invalidated, which re-runs that bridge with the fresh
 * data, replacing the legacy manual refetch.
 *
 * @returns {React.JSX.Element} The rendered ProfileDetails.
 */
function ProfileDetailsContainer() {
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const userId = getUserId();

	const { data: profileResponse, isLoading: isLoadingProfile, error: loadError } = useProfile( userId );

	// Mirror the profile into Redux whenever fresh data arrives, and report eligibility once.
	useEffect( () => {
		if ( ! profileResponse ) {
			return;
		}

		dispatch( receiveUser( profileResponse ) );

		// GTM normally seeds window.dataLayer synchronously from index.html, but guard it
		// anyway so a missing/late-loading GTM snippet can't crash the profile flow.
		window.dataLayer = window.dataLayer || [];

		// This effect re-runs whenever a refetch yields a new object, which `helpScoutSignature`
		// guarantees since it differs per response. Eligibility is a property of the session
		// rather than an event, so report it once per page load — the same guard shape as
		// App.jsx's gtm_myyoast_user push.
		if ( window.dataLayer.some( ( entry ) => entry.event === "is_eligible_for_premium_support" ) ) {
			return;
		}

		window.dataLayer.push( {
			event: "is_eligible_for_premium_support",
			// eslint-disable-next-line camelcase -- GTM data layer key.
			is_eligible_for_premium_support: profileResponse.profile.isEligibleForPremiumSupport,
		} );
	}, [ profileResponse, dispatch ] );

	const patchProfile = usePatchProfile( {
		mutation: {
			onSuccess: () => queryClient.invalidateQueries( { queryKey: getProfileQueryKey( userId ) } ),
		},
	} );

	const attemptSubmitProfile = useCallback( ( firstName, lastName ) => {
		patchProfile.mutate( {
			id: userId,
			// eslint-disable-next-line camelcase -- WordPress profile payload keys.
			data: { first_name: firstName, last_name: lastName },
		} );
	}, [ patchProfile.mutate, userId ] );

	// Gate the form on the profile load so the inputs always mount with the user's name.
	if ( isLoadingProfile ) {
		return <Loader />;
	}

	return (
		<ProfileDetails
			attemptSubmitProfile={ attemptSubmitProfile }
			savingProfile={ patchProfile.isPending }
			profileSaved={ patchProfile.isSuccess }
			saveError={ patchProfile.error }
			loadError={ loadError }
			profile={ profileResponse?.profile }
		/>
	);
}

export default ProfileDetailsContainer;
